import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/Auth/authContext';
import { useI18n } from '../../../contexts/I18nContext';
import { userService } from '../../../services/userService';
import { clientService } from '../../../services/clientService';
import { clientAccessService } from '../../../services/clientAccessService';
import { User, UserCreate } from '../../../types/user';
import { Client } from '../../../types/client';
import config from '../../../config/env';
import { RoleSpace, rolesForSpace } from '../../../utils/roleCatalog';
import { generatePassword, passwordStrength, suggestUsername } from '../passwordTools';
import { canManageRoleAtLevel, highestRoleLevel } from '../roleHierarchy';

// Roles are client roles on the Keycloak client of this environment.
const KEYCLOAK_CLIENT_ID = config.oauth.clientId;

export const USERNAME_RE = /^[a-zA-Z0-9._-]+$/;
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type FormErrors = Record<string, string>;

export interface CreateUserOptions {
  isOpen: boolean;
  onClose: () => void;
  onUserCreated: (user: User) => void;
  /** Preselected client, when opened from inside a client's user list. */
  defaultClientId?: string;
  defaultClientName?: string;
  /** Which catalogue of roles to offer: a customer's technician or platform staff. */
  space: RoleSpace;
}

/**
 * The account creation form: organisation and role, identity (the login
 * is proposed from the names), password (typed or generated), status.
 * Also the way out when the username belongs to a deactivated account:
 * reactivate it instead of inventing a new login.
 */
export const useCreateUser = ({ isOpen, onClose, onUserCreated, defaultClientId, defaultClientName, space }: CreateUserOptions) => {
  const { user: currentUser } = useAuth();
  const { t } = useI18n();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [reactivateTarget, setReactivateTarget] = useState<User | null>(null);

  // Identity
  const [username, setUsername] = useState('');
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // Password and status
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState(true);
  const [enabled, setEnabled] = useState(true);

  // Organisation and role
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedRole, setSelectedRole] = useState('Viewer');

  const isSuperAdmin = useMemo(() => currentUser?.roles?.includes('SuperAdmin') || false, [currentUser?.roles]);
  const callerLevel = useMemo(() => highestRoleLevel(currentUser?.roles), [currentUser?.roles]);

  /** Roles the caller may hand out: never more than they hold. */
  const availableRoles = useMemo(
    () => rolesForSpace(space).filter((role) => canManageRoleAtLevel(role.id, callerLevel, isSuperAdmin)),
    [space, isSuperAdmin, callerLevel],
  );

  /** Clients the caller may create accounts in. */
  const availableClients = useMemo(() => {
    if (isSuperAdmin) return clients;
    const own = currentUser?.clientIds || [];
    return own.length === 0 ? clients : clients.filter((client) => own.includes(client.id));
  }, [clients, isSuperAdmin, currentUser?.clientIds]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoadingClients(true);
    clientService.fetchActiveClients()
      .then((list) => { if (!cancelled) setClients(list); })
      .catch((err) => console.error('[CREATE USER] Failed to fetch clients:', err))
      .finally(() => { if (!cancelled) setLoadingClients(false); });
    return () => { cancelled = true; };
  }, [isOpen]);

  // Opened from a client's user list: that client is the answer.
  useEffect(() => {
    if (isOpen && defaultClientId) setSelectedClientId(defaultClientId);
  }, [isOpen, defaultClientId]);

  // The login follows the names until the operator types their own.
  useEffect(() => {
    if (!usernameTouched) setUsername(suggestUsername(firstName, lastName));
  }, [firstName, lastName, usernameTouched]);

  const clientName = defaultClientName ?? clients.find((c) => c.id === selectedClientId)?.name ?? '';
  const selectedRoleOption = availableRoles.find((r) => r.id === selectedRole);
  const strength = passwordStrength(password);

  /** The catalogue's wording for a role, when Keycloak carries none. */
  const roleDescription = (id: string, fallback: string) => {
    const key = `users.roleDescriptions.${id}` as const;
    const text = t(key as never);
    return text && text !== key ? text : fallback;
  };

  // ------------------------------------------------------------- validation
  const usernameError = (value: string): string => {
    const v = value.trim();
    if (!v) return t('users.create.errUsernameRequired');
    if (v.length < 3) return t('users.create.errUsernameShort');
    if (v.length > 255) return t('users.create.errUsernameLong');
    if (!USERNAME_RE.test(v)) return t('users.create.errUsernameChars');
    return '';
  };

  const validate = (): boolean => {
    const next: FormErrors = {};
    const u = usernameError(username);
    if (u) next.username = u;
    if (!firstName.trim()) next.firstName = t('users.create.errFirstNameRequired');
    if (!lastName.trim()) next.lastName = t('users.create.errLastNameRequired');
    if (!email.trim()) next.email = t('users.create.errEmailRequired');
    else if (!EMAIL_RE.test(email)) next.email = t('users.create.errEmail');
    if (password) {
      if (password.length < 8) next.password = t('users.create.errPasswordShort');
      if (password !== confirmPassword) next.confirmPassword = t('users.create.errPasswordMismatch');
    }
    if (!selectedClientId) next.client = t('users.create.errClientRequired');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const setFieldError = (field: string, message: string) => setErrors((prev) => ({ ...prev, [field]: message }));
  const clearError = (field: string) => { if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' })); };

  const canSubmit = !isLoading && username.trim().length >= 3 && !!selectedClientId
    && firstName.trim().length > 0 && lastName.trim().length > 0 && EMAIL_RE.test(email.trim())
    && (!password || password === confirmPassword);

  // ------------------------------------------------------------- actions
  /**
   * Give the user access to the selected client on this environment, and
   * the selected role. Failures are thrown: a user without access or role
   * would see a broken app with no explanation.
   */
  const applyClientAndRole = async (userId?: string) => {
    if (!userId) return;
    if (selectedClientId) await clientAccessService.grantAccess(selectedClientId, userId);
    if (selectedRole) await userService.addClientRoleToUser(userId, KEYCLOAK_CLIENT_ID, selectedRole);
  };

  const reset = () => {
    setUsername('');
    setUsernameTouched(false);
    setEmail('');
    setFirstName('');
    setLastName('');
    setPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setTemporaryPassword(true);
    setEnabled(true);
    setSelectedClientId('');
    setSelectedRole('Viewer');
    setErrors({});
    setError(null);
    setReactivateTarget(null);
  };

  const close = () => {
    if (isLoading) return;
    reset();
    onClose();
  };

  /** Re-enable the deactivated account holding this username, with the form's client and role. */
  const reactivate = async () => {
    if (!reactivateTarget) return;
    setIsLoading(true);
    setError(null);
    try {
      const revived = await userService.updateUser(reactivateTarget.id, {
        enabled: true,
        ...(email ? { email: email.trim() } : {}),
        ...(firstName ? { firstName: firstName.trim() } : {}),
        ...(lastName ? { lastName: lastName.trim() } : {}),
      });
      await applyClientAndRole(reactivateTarget.id);
      if (password) await userService.resetPassword(reactivateTarget.id, { password, temporary: temporaryPassword });
      onUserCreated(revived ?? reactivateTarget);
      reset();
      onClose();
    } catch (err: any) {
      console.error('[CREATE USER] Reactivation failed:', err);
      setError(err?.message || t('users.create.errReactivate'));
    } finally {
      setIsLoading(false);
    }
  };

  /** The message for a creation failure; a taken username may belong to a deactivated account. */
  const explainCreateError = async (err: any): Promise<string> => {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') {
      if (detail.includes('already exists') || detail.includes('duplicate') || detail.includes('deactivated account')) {
        try {
          const existing = await userService.getUserByUsername(username.trim());
          if (existing && existing.enabled === false) {
            setReactivateTarget(existing);
            return t('users.create.errUsernameDeactivated', { username });
          }
        } catch {
          // Lookup is best effort: outside the caller's scope it returns 403.
        }
        return t('users.create.errUsernameTaken', { username });
      }
      return detail;
    }
    if (Array.isArray(detail)) return detail.map((d: any) => `${d.loc?.join('.')}: ${d.msg}`).join(', ');
    return err.message || t('users.create.errCreate');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setReactivateTarget(null);
    if (!validate()) return;
    setIsLoading(true);
    try {
      const userData: UserCreate = {
        username: username.trim(), enabled,
        email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim(),
      };
      if (password) {
        userData.credentials = [{ type: 'password', value: password, temporary: temporaryPassword }];
        if (temporaryPassword) userData.requiredActions = ['UPDATE_PASSWORD'];
      }
      const created = await userService.createUser(userData);
      // The user exists in Keycloak from here: a failure below is surfaced,
      // and the detail view (add to group, assign role) recovers.
      await applyClientAndRole(created.id);
      onUserCreated(created);
      reset();
      onClose();
    } catch (err: any) {
      console.error('[CREATE USER] Error creating user:', err);
      setError(await explainCreateError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const fillGeneratedPassword = () => {
    const generated = generatePassword();
    setPassword(generated);
    setConfirmPassword(generated);
    setShowPassword(true);
    clearError('password');
    clearError('confirmPassword');
  };

  return {
    isLoading, error, setError, errors, setFieldError, clearError, reactivateTarget, reactivate,
    username, setUsername, usernameTouched, setUsernameTouched, usernameError, email, setEmail, firstName, setFirstName, lastName, setLastName,
    password, setPassword, confirmPassword, setConfirmPassword, showPassword, setShowPassword, temporaryPassword, setTemporaryPassword, enabled, setEnabled,
    clients, loadingClients, availableClients, availableRoles, selectedClientId, setSelectedClientId, selectedRole, setSelectedRole,
    clientName, selectedRoleOption, strength, roleDescription, canSubmit, submit, close, fillGeneratedPassword,
    defaultClientId,
  };
};

export type CreateUserState = ReturnType<typeof useCreateUser>;
